import React, { useMemo, useState, useEffect, useRef } from 'react';
import { getWeekDateRange, getWeeksInYear } from '../utils/weekCalculations';

/**
 * TaskGanttView — Phase 2
 *
 * Resource-axis Gantt chart for the admin Tasks page.
 * - Y-axis: team members (active users from the filtered task set) + a pinned
 *   "Unassigned" row at the top when any assigned-to-nobody tasks are present.
 * - X-axis: weeks of `selectedYear`, bounded by the existing fromWeek/toWeek
 *   filter so admins can zoom in on a window via the year selector + filters.
 * - Bars: option (a) for Phase 2 — span from start_date to due_date inclusive.
 *   One-date tasks render as a 1-day chevron marker on the available date.
 *   No-date tasks live in the Unscheduled panel below the chart.
 * - Multi-assignee tasks render once per assignee row (same record, projected).
 * - Click any bar / chevron / unscheduled row → opens the existing Edit modal
 *   via the `onTaskClick` callback supplied by the parent.
 *
 * Phase 3 hooks (already scaffolded, NOT activated):
 * - Each rendered bar receives a `mode` prop set to `'date-range'` today.
 *   Phase 3 will introduce `'overlay'` to draw a darker capacity overlay
 *   inside the date-range bar without changing the component contract.
 * - `hasCapacityOverflow` is computed per task and surfaced via the tooltip
 *   warning today. Phase 3 will use the same flag to drive a hatched overflow
 *   visualisation extending past the bar's right edge.
 *
 * Statuses use the existing list-view palette. Days are 1-indexed within
 * a week starting Monday.
 */

const STATUS_BAR_COLORS = {
  pending: { bg: 'bg-amber-500', hover: 'hover:bg-amber-400', text: 'text-amber-50', label: 'Pending' },
  in_progress: { bg: 'bg-blue-500', hover: 'hover:bg-blue-400', text: 'text-blue-50', label: 'In progress' },
  completed: { bg: 'bg-emerald-600', hover: 'hover:bg-emerald-500', text: 'text-emerald-50', label: 'Completed' },
  cancelled: { bg: 'bg-slate-500', hover: 'hover:bg-slate-400', text: 'text-slate-100', label: 'Cancelled' },
};

const DEFAULT_STATUS = STATUS_BAR_COLORS.pending;

// ---------- Date helpers ----------

const parseISODateOnly = (value) => {
  if (!value) return null;
  // Accept both 'YYYY-MM-DD' and full ISO; normalise to midnight UTC for stable comparison.
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
};

const utcDayDiff = (a, b) => {
  if (!a || !b) return 0;
  return Math.round((a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24));
};

const inclusiveDayCount = (start, end) => {
  if (!start || !end) return 0;
  return Math.max(1, utcDayDiff(end, start) + 1);
};

const fmtDate = (d) => {
  if (!d) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ---------- Bar component ----------

const Bar = ({
  task,
  startCol,    // 1-indexed column (days from chart start, +1)
  spanCols,    // number of day columns to span
  dayWidthPx,
  rowHeightPx,
  laneIndex,   // overlap stacking lane (0 = first lane)
  mode = 'date-range',
  onClick,
  onDragStart,  // ({task, mode, anchorClientX}) — fired on mousedown of bar/handle
  justDraggedRef, // ref<boolean> — true briefly after a real drag, used to swallow synthetic click
  isDragging,   // true while parent is mid-drag of *this* task
  dragOffsetDays = 0,  // optimistic preview offset while dragging (whole bar move)
  dragStartDelta = 0,  // optimistic delta on start_date while resizing left handle
  dragEndDelta = 0,    // optimistic delta on due_date while resizing right handle
}) => {
  const status = STATUS_BAR_COLORS[task.status] || DEFAULT_STATUS;
  const isChevron = task._ganttIsChevron;

  // Apply optimistic drag offsets to the visual position only.
  const effectiveStartCol = startCol + dragOffsetDays + dragStartDelta;
  const effectiveSpan = Math.max(1, spanCols - dragStartDelta + dragEndDelta);

  const left = (effectiveStartCol - 1) * dayWidthPx;
  const width = isChevron ? dayWidthPx : Math.max(dayWidthPx, effectiveSpan * dayWidthPx);
  const top = laneIndex * (rowHeightPx + 4) + 4;

  // Tooltip title — full info, browser-rendered on hover
  const tooltipLines = [
    task.title,
    task.site_name ? `Site: ${task.site_name}` : null,
    `${status.label}${task.priority ? `  •  ${task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}` : ''}`,
    `Start: ${fmtDate(task._ganttStart)}`,
    `Due:   ${fmtDate(task._ganttEnd)}`,
    task.task_capacity != null ? `Workload: ${Number(task.task_capacity).toFixed(2)} day${Number(task.task_capacity) === 1 ? '' : 's'}` : null,
    task._ganttHasCapacityOverflow
      ? `⚠ Workload (${Number(task.task_capacity).toFixed(2)} days) exceeds scheduled window (${task._ganttScheduledDays} day${task._ganttScheduledDays === 1 ? '' : 's'})`
      : null,
  ].filter(Boolean);
  const tooltipText = tooltipLines.join('\n');

  // Show inline title only when bar is wide enough (~3+ chars readable)
  const showInlineTitle = !isChevron && width >= 56;

  if (isChevron) {
    // 1-day chevron marker. Direction depends on which date is set.
    const chevronDir = task._ganttChevronType; // 'start' | 'due'
    return (
      <button
        type="button"
        onClick={(e) => { if (justDraggedRef?.current || isDragging) return; onClick?.(task); }}
        onMouseDown={onDragStart ? (e) => {
          if (e.button !== 0) return;
          e.stopPropagation();
          onDragStart({ task, mode: 'move', anchorClientX: e.clientX });
        } : undefined}
        title={tooltipText}
        aria-label={tooltipLines.join('. ')}
        data-mode={mode}
        data-testid={`gantt-task-marker-${task.id}`}
        className={`absolute flex items-center justify-center ${status.bg} ${status.hover} ${status.text} rounded-sm shadow-md transition-transform duration-150 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-purple-400 ${onDragStart ? 'cursor-grab active:cursor-grabbing' : ''} ${isDragging ? 'opacity-80 ring-2 ring-purple-300' : ''}`}
        style={{
          left: `${left + dayWidthPx * 0.1}px`,
          width: `${dayWidthPx * 0.8}px`,
          height: `${rowHeightPx - 4}px`,
          top: `${top}px`,
          clipPath: chevronDir === 'due'
            ? 'polygon(0 0, 100% 0, 100% 100%, 50% 75%, 0 100%)'
            : 'polygon(50% 25%, 100% 0, 100% 100%, 0 100%, 0 0)',
        }}
      >
        {task._ganttHasCapacityOverflow && (
          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-rose-500 border border-slate-900" aria-hidden="true" />
        )}
      </button>
    );
  }

  // Workload overlay (Phase 3): a darker band inside the bar covering
  // `task_capacity` days from the start of the range. If capacity > scheduled
  // window, the overlay extends past the bar's right edge as a hatched stub.
  const capacity = Number(task.task_capacity) || 0;
  const scheduledDays = Math.max(1, spanCols);
  const overlayDays = Math.min(capacity, scheduledDays);
  const overflowDays = Math.max(0, capacity - scheduledDays);
  const overlayWidthPx = overlayDays > 0 ? overlayDays * dayWidthPx - 4 : 0;
  const overflowWidthPx = overflowDays > 0 ? overflowDays * dayWidthPx : 0;
  const showOverlay = mode === 'overlay' && capacity > 0;

  return (
    <button
      type="button"
      onClick={(e) => { if (justDraggedRef?.current || isDragging) return; onClick?.(task); }}
      onMouseDown={onDragStart ? (e) => {
        if (e.button !== 0) return;
        // Only fire move-drag from the bar body, not the resize handles
        if (e.target.dataset.handle) return;
        e.stopPropagation();
        onDragStart({ task, mode: 'move', anchorClientX: e.clientX });
      } : undefined}
      title={tooltipText}
      aria-label={tooltipLines.join('. ')}
      data-mode={mode}
      data-testid={`gantt-task-bar-${task.id}`}
      className={`absolute ${status.bg} ${status.hover} ${status.text} rounded-md shadow-md transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-400 overflow-visible text-left pl-2 pr-2 ${onDragStart ? 'cursor-grab active:cursor-grabbing hover:brightness-110' : 'hover:brightness-110'} ${isDragging ? 'opacity-80 ring-2 ring-purple-300 z-20' : ''}`}
      style={{
        left: `${left + 2}px`,
        width: `${width - 4}px`,
        height: `${rowHeightPx - 4}px`,
        top: `${top}px`,
      }}
    >
      {/* Workload overlay (darker shade for committed work) */}
      {showOverlay && overlayWidthPx > 0 && (
        <span
          className="absolute top-0 left-0 bottom-0 bg-black/30 rounded-l-md pointer-events-none"
          style={{ width: `${overlayWidthPx}px` }}
          aria-hidden="true"
        />
      )}
      {/* Workload overflow stub (hatched rose) — extends past the right edge */}
      {showOverlay && overflowWidthPx > 0 && (
        <span
          className="absolute top-0 bottom-0 pointer-events-none rounded-r-md"
          style={{
            left: `${width - 4}px`,
            width: `${overflowWidthPx}px`,
            background: 'repeating-linear-gradient(45deg, rgba(244,63,94,0.7) 0 4px, rgba(244,63,94,0.4) 4px 8px)',
          }}
          aria-hidden="true"
        />
      )}
      {/* Inline title */}
      {showInlineTitle && (
        <span className="relative z-10 block text-xs font-medium leading-none whitespace-nowrap overflow-hidden text-ellipsis" style={{ lineHeight: `${rowHeightPx - 4}px` }}>
          {task.title}
        </span>
      )}
      {/* Fallback warning dot when overlay mode is off */}
      {!showOverlay && task._ganttHasCapacityOverflow && (
        <span className="absolute top-0.5 right-1 w-2 h-2 rounded-full bg-rose-500 border border-white/50 z-20" aria-hidden="true" />
      )}
      {/* Resize handles (drag-to-reschedule, Phase 3) */}
      {onDragStart && (
        <>
          <span
            data-handle="start"
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              e.stopPropagation();
              onDragStart({ task, mode: 'resize-start', anchorClientX: e.clientX });
            }}
            className="absolute top-0 bottom-0 left-0 w-1.5 cursor-ew-resize hover:bg-white/30 z-30"
            aria-hidden="true"
          />
          <span
            data-handle="end"
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              e.stopPropagation();
              onDragStart({ task, mode: 'resize-end', anchorClientX: e.clientX });
            }}
            className="absolute top-0 bottom-0 right-0 w-1.5 cursor-ew-resize hover:bg-white/30 z-30"
            aria-hidden="true"
          />
        </>
      )}
    </button>
  );
};

// ---------- Lane packing (overlap stacking within a row) ----------

const packLanes = (entries) => {
  // entries: [{ startCol, endCol, ...task }] (endCol inclusive)
  // Returns same entries with `laneIndex` assigned, lowest available lane per overlap.
  const sorted = [...entries].sort((a, b) => a.startCol - b.startCol || a.endCol - b.endCol);
  const laneTails = []; // laneTails[i] = last used endCol on lane i
  for (const e of sorted) {
    let assigned = -1;
    for (let i = 0; i < laneTails.length; i++) {
      if (laneTails[i] < e.startCol) {
        assigned = i;
        break;
      }
    }
    if (assigned === -1) {
      assigned = laneTails.length;
      laneTails.push(e.endCol);
    } else {
      laneTails[assigned] = e.endCol;
    }
    e.laneIndex = assigned;
  }
  return { entries: sorted, lanesUsed: Math.max(1, laneTails.length) };
};

// ---------- Main view ----------

const TaskGanttView = ({
  tasks,
  users,
  selectedYear,
  fromWeek,
  toWeek,
  onTaskClick,
  onTaskReschedule,  // (taskId, { start_date, due_date }) => Promise — Phase 3 drag-to-reschedule hook
  scrollToCurrentWeekSignal = 0,
}) => {
  const [unscheduledOpen, setUnscheduledOpen] = useState(true);
  const scrollWrapperRef = useRef(null);
  const hasInitialScrolledRef = useRef(false);

  // Zoom level — Day / Week (default) / Month. Persisted to localStorage.
  const [zoom, setZoom] = useState(() => {
    try {
      const v = localStorage.getItem('ganttZoom');
      return ['day', 'week', 'month'].includes(v) ? v : 'week';
    } catch {
      return 'week';
    }
  });
  useEffect(() => {
    try { localStorage.setItem('ganttZoom', zoom); } catch { /* ignore */ }
  }, [zoom]);

  // Drag-to-reschedule state (Phase 3). Listeners are registered below,
  // after dayWidthPx is defined.
  const [dragState, setDragState] = useState(null);
  const dragStateRef = useRef(null);
  useEffect(() => { dragStateRef.current = dragState; }, [dragState]);
  // Set briefly after a real drag (moved >= 1 day) so the trailing synthetic
  // click on mouseup is suppressed and the Edit modal doesn't open.
  const justDraggedRef = useRef(false);

  const handleBarDragStart = ({ task: t, mode: m, anchorClientX }) => {
    setDragState({ task: t, mode: m, anchorClientX, deltaDays: 0 });
  };

  // 1. Compute the chart's date span from the week filter.
  const chartStart = useMemo(() => getWeekDateRange(selectedYear, Math.max(1, fromWeek)).start, [selectedYear, fromWeek]);
  const chartEnd = useMemo(() => {
    const maxWeek = Math.min(getWeeksInYear(selectedYear), toWeek);
    return getWeekDateRange(selectedYear, maxWeek).end;
  }, [selectedYear, toWeek]);

  const chartStartUTC = useMemo(() => parseISODateOnly(chartStart.toISOString()), [chartStart]);
  const chartEndUTC = useMemo(() => parseISODateOnly(chartEnd.toISOString()), [chartEnd]);
  const totalDays = inclusiveDayCount(chartStartUTC, chartEndUTC);

  // Column sizing — driven by zoom level.
  //   day:   ~50px / day (≈ 30 days in 1500px viewport)
  //   week:  22px  / day (≈ 9 weeks in 1500px viewport — default)
  //   month: 6px   / day (≈ 7 months in 1500px viewport — quarterly overview)
  const dayWidthPx = zoom === 'day' ? 50 : zoom === 'month' ? 6 : 22;
  const rowHeightPx = 28;
  const labelColPx = 180;
  const chartWidthPx = totalDays * dayWidthPx;

  // Window-level listeners for drag move / up. Translates pixel motion into
  // whole-day deltas (snapping). Commits via onTaskReschedule on mouse up.
  useEffect(() => {
    if (!dragState) return;
    const dwp = dayWidthPx;
    const handleMove = (e) => {
      const cur = dragStateRef.current;
      if (!cur) return;
      const dxPx = e.clientX - cur.anchorClientX;
      const days = Math.round(dxPx / dwp);
      if (days !== cur.deltaDays) {
        const next = { ...cur, deltaDays: days };
        dragStateRef.current = next;
        setDragState(next);
      }
    };
    const handleUp = async () => {
      const cur = dragStateRef.current;
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      if (!cur) { setDragState(null); return; }
      const { task: t, mode: m, deltaDays } = cur;
      setDragState(null);
      // If the drag actually moved, suppress the trailing synthetic click
      // so the Edit modal doesn't open on release.
      if (deltaDays !== 0) {
        justDraggedRef.current = true;
        setTimeout(() => { justDraggedRef.current = false; }, 0);
      }
      if (!deltaDays || !onTaskReschedule) return;
      const shiftDays = (iso, n) => {
        if (!iso) return null;
        const d = new Date(iso);
        d.setUTCDate(d.getUTCDate() + n);
        return d.toISOString();
      };
      let payload = {};
      if (m === 'move') {
        if (t.start_date) payload.start_date = shiftDays(t.start_date, deltaDays);
        if (t.due_date) payload.due_date = shiftDays(t.due_date, deltaDays);
      } else if (m === 'resize-start') {
        if (t.start_date) payload.start_date = shiftDays(t.start_date, deltaDays);
        else if (t.due_date) payload.start_date = shiftDays(t.due_date, deltaDays);
      } else if (m === 'resize-end') {
        if (t.due_date) payload.due_date = shiftDays(t.due_date, deltaDays);
        else if (t.start_date) payload.due_date = shiftDays(t.start_date, deltaDays);
      }
      if (Object.keys(payload).length === 0) return;
      try {
        await onTaskReschedule(t.id, payload);
      } catch (err) {
        // Parent surfaces errors via modal.
      }
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [dragState, dayWidthPx, onTaskReschedule]);

  // 2. Bucket tasks into scheduled (renderable on Gantt) vs unscheduled.
  const { scheduledByAssignee, unscheduled, unassignedScheduled } = useMemo(() => {
    const byAssignee = new Map(); // user_id -> entries[]
    const unassigned = [];
    const noDate = [];

    for (const task of tasks) {
      const start = parseISODateOnly(task.start_date);
      const end = parseISODateOnly(task.due_date);

      if (!start && !end) {
        noDate.push(task);
        continue;
      }

      const isChevron = !(start && end);
      const ganttStart = start || end;
      const ganttEnd = end || start;

      // Clip to chart window — skip if entirely outside.
      if (ganttEnd < chartStartUTC || ganttStart > chartEndUTC) {
        continue;
      }
      const clippedStart = ganttStart < chartStartUTC ? chartStartUTC : ganttStart;
      const clippedEnd = ganttEnd > chartEndUTC ? chartEndUTC : ganttEnd;
      const startCol = utcDayDiff(clippedStart, chartStartUTC) + 1;
      const endCol = utcDayDiff(clippedEnd, chartStartUTC) + 1;

      // Scaffolded Phase 3 flag — informational today, drives overlay later.
      const scheduledDays = inclusiveDayCount(ganttStart, ganttEnd);
      const capacity = Number(task.task_capacity) || 0;
      const hasCapacityOverflow = capacity > 0 && capacity > scheduledDays;

      const entry = {
        ...task,
        _ganttStart: ganttStart,
        _ganttEnd: ganttEnd,
        _ganttIsChevron: isChevron,
        _ganttChevronType: isChevron ? (end && !start ? 'due' : 'start') : null,
        _ganttScheduledDays: scheduledDays,
        _ganttHasCapacityOverflow: hasCapacityOverflow,
        startCol,
        endCol,
      };

      const assignees = Array.isArray(task.assigned_to) ? task.assigned_to : [];
      if (assignees.length === 0) {
        unassigned.push(entry);
      } else {
        for (const userId of assignees) {
          if (!byAssignee.has(userId)) byAssignee.set(userId, []);
          // Clone per assignee so lane packing is independent.
          byAssignee.get(userId).push({ ...entry });
        }
      }
    }

    return { scheduledByAssignee: byAssignee, unscheduled: noDate, unassignedScheduled: unassigned };
  }, [tasks, chartStartUTC, chartEndUTC]);

  // 3. Build row list: pinned "Unassigned" first (if any scheduled unassigned), then users alphabetically that have at least one entry.
  const rows = useMemo(() => {
    const result = [];
    if (unassignedScheduled.length > 0) {
      result.push({
        rowId: '__unassigned__',
        label: 'Unassigned',
        sublabel: `${unassignedScheduled.length} task${unassignedScheduled.length === 1 ? '' : 's'}`,
        isUnassigned: true,
        entries: unassignedScheduled,
      });
    }

    const usersWithTasks = [...scheduledByAssignee.entries()]
      .map(([userId, entries]) => {
        const user = users.find((u) => u.id === userId);
        const label = user?.full_name || user?.first_name || 'Unknown user';
        return { rowId: userId, label, sublabel: '', isUnassigned: false, entries };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    return [...result, ...usersWithTasks];
  }, [scheduledByAssignee, unassignedScheduled, users]);

  // 4. Lane-pack each row to handle overlaps.
  const rowsWithLanes = useMemo(() => {
    return rows.map((row) => {
      const { entries, lanesUsed } = packLanes(row.entries);
      const height = lanesUsed * (rowHeightPx + 4) + 4;
      return { ...row, entries, lanesUsed, height };
    });
  }, [rows]);

  // 5. Build week-header cells (driven by week filter, same axis as list view).
  const weekHeaders = useMemo(() => {
    const list = [];
    const max = Math.min(getWeeksInYear(selectedYear), toWeek);
    for (let w = Math.max(1, fromWeek); w <= max; w++) {
      const { start, startStr, endStr } = getWeekDateRange(selectedYear, w);
      const startUtc = parseISODateOnly(start.toISOString());
      const dayOffset = utcDayDiff(startUtc, chartStartUTC);
      list.push({
        weekNumber: w,
        startCol: dayOffset + 1,
        startStr,
        endStr,
      });
    }
    return list;
  }, [selectedYear, fromWeek, toWeek, chartStartUTC]);

  // 6. Today marker (only if today falls within the chart window).
  const todayCol = useMemo(() => {
    const today = parseISODateOnly(new Date().toISOString());
    if (!today || today < chartStartUTC || today > chartEndUTC) return null;
    return utcDayDiff(today, chartStartUTC) + 1;
  }, [chartStartUTC, chartEndUTC]);

  // 7. Auto-scroll to current week on initial mount (instant, no flash),
  //    and on each subsequent bump of `scrollToCurrentWeekSignal` (smooth).
  //    Task fetches / drag-drop / edits do NOT increment the signal, so
  //    the user's scroll position is preserved across data updates.
  // 7. Auto-scroll horizontally to the current week:
  //    Effect A: ONE-TIME on initial render where the wrapper exists. Runs on
  //              every commit and short-circuits once done, so it copes with
  //              the wrapper appearing later (e.g. after async task fetch).
  //    Effect B: SIGNAL-DRIVEN (Current Week button). Smoothly scrolls each
  //              time the parent bumps `scrollToCurrentWeekSignal`.
  //    Task fetches / drag-drop / edits do NOT bump the signal, so the user's
  //    scroll position is preserved across data updates.
  useEffect(() => {
    if (hasInitialScrolledRef.current) return;
    const el = scrollWrapperRef.current;
    if (!el || todayCol === null) return;
    const target = (todayCol - 1) * dayWidthPx + dayWidthPx / 2 - el.clientWidth / 2 + labelColPx / 2;
    el.scrollTo({ left: Math.max(0, target), behavior: 'auto' });
    hasInitialScrolledRef.current = true;
  });

  useEffect(() => {
    if (scrollToCurrentWeekSignal === 0) return;
    const el = scrollWrapperRef.current;
    if (!el || todayCol === null) return;
    const target = (todayCol - 1) * dayWidthPx + dayWidthPx / 2 - el.clientWidth / 2 + labelColPx / 2;
    el.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [scrollToCurrentWeekSignal, todayCol, dayWidthPx, labelColPx]);

  // Group unscheduled tasks by assignee for the panel below.
  const unscheduledByAssignee = useMemo(() => {
    const map = new Map();
    for (const t of unscheduled) {
      const assignees = Array.isArray(t.assigned_to) ? t.assigned_to : [];
      if (assignees.length === 0) {
        if (!map.has('__unassigned__')) map.set('__unassigned__', []);
        map.get('__unassigned__').push(t);
      } else {
        for (const uid of assignees) {
          if (!map.has(uid)) map.set(uid, []);
          map.get(uid).push(t);
        }
      }
    }
    return map;
  }, [unscheduled]);

  const userLookup = useMemo(() => {
    const m = new Map();
    for (const u of users) m.set(u.id, u);
    return m;
  }, [users]);

  // Empty state
  const hasAnyScheduled = rowsWithLanes.length > 0;

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 min-w-0 overflow-hidden" data-testid="task-gantt-view">
      {/* Legend + Zoom controls */}
      <div className="flex items-center flex-wrap gap-3 mb-3 text-xs text-gray-400">
        <span className="font-medium text-gray-300">Status:</span>
        {Object.entries(STATUS_BAR_COLORS).map(([key, val]) => (
          <span key={key} className="flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded ${val.bg}`} aria-hidden="true" />
            <span>{val.label}</span>
          </span>
        ))}
        <span className="flex items-center gap-1.5 ml-2">
          <span
            className="inline-block w-4 h-3 rounded-sm"
            style={{ background: 'repeating-linear-gradient(45deg, rgba(244,63,94,0.7) 0 3px, rgba(244,63,94,0.4) 3px 6px)' }}
            aria-hidden="true"
          />
          <span>Workload exceeds scheduled window</span>
        </span>
        <span className="flex items-center gap-1.5 ml-2 text-gray-500">
          <span>◀</span><span>start-only</span><span className="mx-1">·</span><span>due-only</span><span>▶</span>
        </span>

        <span className="ml-auto flex items-center gap-2">
          <span className="text-gray-300 font-medium">Zoom:</span>
          <div className="inline-flex items-center bg-slate-700 border border-slate-600 rounded-md p-0.5" data-testid="gantt-zoom-toggle">
            {['day', 'week', 'month'].map((z) => (
              <button
                key={z}
                type="button"
                onClick={() => setZoom(z)}
                className={`px-2 py-0.5 rounded text-xs font-medium capitalize transition-colors ${
                  zoom === z ? 'bg-purple-600 text-white' : 'text-gray-300 hover:text-white'
                }`}
                data-testid={`gantt-zoom-${z}-btn`}
              >
                {z}
              </button>
            ))}
          </div>
        </span>
      </div>

      {!hasAnyScheduled && unscheduled.length === 0 ? (
        <div className="py-12 text-center text-gray-400 text-sm" data-testid="gantt-empty-state">
          No scheduled tasks in this view. Add a Start Date or Due Date to see tasks on the timeline.
        </div>
      ) : (
        <div ref={scrollWrapperRef} className="w-full max-w-full overflow-auto max-h-[calc(100vh-320px)] border border-slate-700 rounded-lg">
          {/* Header row: week markers across the top, with a fixed label column */}
          <div className="sticky top-0 z-20 bg-slate-900 border-b border-slate-700 flex" style={{ width: `${labelColPx + chartWidthPx}px`, minWidth: `${labelColPx + chartWidthPx}px` }}>
            <div
              className="flex-shrink-0 sticky left-0 z-30 bg-slate-900 border-r border-slate-700 px-3 py-2 text-xs uppercase tracking-wide text-gray-400 font-medium"
              style={{ width: `${labelColPx}px` }}
            >
              Team member
            </div>
            <div className="relative" style={{ width: `${chartWidthPx}px`, minWidth: `${chartWidthPx}px` }}>
              <div className="flex h-10">
                {weekHeaders.map((wk, idx) => {
                  const nextStart = weekHeaders[idx + 1]?.startCol ?? totalDays + 1;
                  const widthPx = (nextStart - wk.startCol) * dayWidthPx;
                  return (
                    <div
                      key={wk.weekNumber}
                      className="border-r border-slate-700 flex flex-col justify-center px-2 text-[10px] text-gray-400"
                      style={{ width: `${widthPx}px`, minWidth: `${widthPx}px` }}
                      title={`Week ${wk.weekNumber} — ${wk.startStr} to ${wk.endStr}`}
                    >
                      <span className="text-gray-300 font-semibold">W{wk.weekNumber}</span>
                      <span className="text-gray-500">{wk.startStr}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Body rows */}
          {!hasAnyScheduled ? (
            <div className="py-8 text-center text-gray-400 text-sm">No scheduled tasks in this view.</div>
          ) : (
            <div style={{ width: `${labelColPx + chartWidthPx}px`, minWidth: `${labelColPx + chartWidthPx}px` }}>
              {rowsWithLanes.map((row) => (
                <div
                  key={row.rowId}
                  className={`flex border-b border-slate-700 ${row.isUnassigned ? 'bg-amber-950/30' : 'bg-slate-900/40'}`}
                  data-testid={`gantt-row-${row.rowId}`}
                >
                  <div
                    className="flex-shrink-0 sticky left-0 z-10 px-3 py-2 border-r border-slate-700 flex flex-col justify-center"
                    style={{ width: `${labelColPx}px`, backgroundColor: row.isUnassigned ? 'rgba(120, 53, 15, 0.35)' : 'rgb(15, 23, 42, 0.95)' }}
                  >
                    <span className={`text-sm font-medium truncate ${row.isUnassigned ? 'text-amber-200' : 'text-gray-200'}`}>{row.label}</span>
                    {row.sublabel && <span className="text-xs text-gray-500 truncate">{row.sublabel}</span>}
                  </div>
                  <div
                    className="relative"
                    style={{ width: `${chartWidthPx}px`, minWidth: `${chartWidthPx}px`, height: `${row.height}px` }}
                  >
                    {/* Weekend / week stripes */}
                    {weekHeaders.map((wk, idx) => {
                      const nextStart = weekHeaders[idx + 1]?.startCol ?? totalDays + 1;
                      const left = (wk.startCol - 1) * dayWidthPx;
                      const widthPx = (nextStart - wk.startCol) * dayWidthPx;
                      return (
                        <div
                          key={wk.weekNumber}
                          className={`absolute top-0 bottom-0 border-r border-slate-800 ${idx % 2 === 0 ? 'bg-slate-900/30' : 'bg-transparent'}`}
                          style={{ left: `${left}px`, width: `${widthPx}px` }}
                          aria-hidden="true"
                        />
                      );
                    })}
                    {todayCol !== null && (
                      <div
                        className="absolute top-0 bottom-0 w-px bg-purple-400/80 z-[1]"
                        style={{ left: `${(todayCol - 1) * dayWidthPx + dayWidthPx / 2}px` }}
                        aria-hidden="true"
                      />
                    )}
                    {row.entries.map((entry) => {
                      const isThisDragging = dragState && dragState.task.id === entry.id;
                      const mv = dragState && dragState.mode === 'move' ? dragState.deltaDays : 0;
                      const sd = dragState && dragState.mode === 'resize-start' ? dragState.deltaDays : 0;
                      const ed = dragState && dragState.mode === 'resize-end' ? dragState.deltaDays : 0;
                      return (
                        <Bar
                          key={`${entry.id}-${row.rowId}`}
                          task={entry}
                          startCol={entry.startCol}
                          spanCols={Math.max(1, entry.endCol - entry.startCol + 1)}
                          dayWidthPx={dayWidthPx}
                          rowHeightPx={rowHeightPx}
                          laneIndex={entry.laneIndex}
                          mode="overlay"
                          onClick={onTaskClick}
                          onDragStart={onTaskReschedule ? handleBarDragStart : undefined}
                          justDraggedRef={justDraggedRef}
                          isDragging={isThisDragging}
                          dragOffsetDays={isThisDragging ? mv : 0}
                          dragStartDelta={isThisDragging ? sd : 0}
                          dragEndDelta={isThisDragging ? ed : 0}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Unscheduled panel */}
      {unscheduled.length > 0 && (
        <div className="mt-4 border border-slate-700 rounded-lg bg-slate-900/60" data-testid="gantt-unscheduled-panel">
          <button
            type="button"
            onClick={() => setUnscheduledOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-800/60 transition-colors"
            data-testid="gantt-unscheduled-toggle"
          >
            <span className="flex items-center gap-2">
              <svg className={`w-4 h-4 text-gray-400 transition-transform ${unscheduledOpen ? 'rotate-90' : ''}`} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M7 5l6 5-6 5V5z" />
              </svg>
              <span className="text-sm font-medium text-gray-200">Unscheduled</span>
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-semibold rounded-full bg-amber-600 text-amber-50">
                {unscheduled.length}
              </span>
            </span>
            <span className="text-xs text-gray-500">Open a task to add a Start or Due Date</span>
          </button>
          {unscheduledOpen && (
            <div className="px-4 pb-4 space-y-3">
              {[...unscheduledByAssignee.entries()]
                .map(([uid, items]) => {
                  const user = uid === '__unassigned__' ? null : userLookup.get(uid);
                  return { uid, items, label: user ? user.full_name : 'Unassigned', sortKey: user ? user.full_name : '\u0000' };
                })
                .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
                .map(({ uid, items, label }) => (
                  <div key={uid}>
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-1.5">{label}</div>
                    <div className="flex flex-wrap gap-2">
                      {items.map((t) => {
                        const status = STATUS_BAR_COLORS[t.status] || DEFAULT_STATUS;
                        return (
                          <button
                            type="button"
                            key={t.id}
                            onClick={() => onTaskClick?.(t)}
                            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md ${status.bg} ${status.hover} ${status.text} text-xs font-medium shadow transition-transform duration-150 hover:scale-[1.02]`}
                            data-testid={`gantt-unscheduled-task-${t.id}`}
                            title={`${t.title}${t.site_name ? `\nSite: ${t.site_name}` : ''}`}
                          >
                            <span className="max-w-[200px] truncate">{t.title}</span>
                            {t.site_name && <span className="text-[10px] opacity-80 max-w-[100px] truncate">· {t.site_name}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskGanttView;
